import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
  ORDERED_LIST,
  Transformer,
  UNORDERED_LIST,
} from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { IMAGE_TRANSFORMER, UNDERLINE_TRANSFORMER } from '../transformers';
import { fixLineBreaks, normalizeLineEndings } from '../utils';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorState } from 'lexical';

type MarkdownValuePluginProps = {
  allowImages: boolean;
  allowLinks: boolean;
  allowLists: boolean;
  value?: string;
  onChange?: (newValue: string) => void;
};

export const MarkdownValuePlugin: FC<MarkdownValuePluginProps> = ({
  allowImages,
  allowLinks,
  allowLists,
  value,
  onChange,
}) => {
  const [editor] = useLexicalComposerContext();
  const markdownRef = useRef('');

  // element and text-match transformers create nodes that must be registered on the editor. Only add
  // the ones whose nodes are available, otherwise $convertFromMarkdownString throws and the editor
  // update is discarded, leaving the editor empty. The standard TRANSFORMERS set cannot be used
  // because it also covers headings, quotes and code blocks, which this editor does not support.
  const transformers = useMemo(() => {
    const result: Transformer[] = [];
    if (allowLists) {
      result.push(UNORDERED_LIST, ORDERED_LIST);
    }
    if (allowImages) {
      result.push(IMAGE_TRANSFORMER);
    }
    // text formats are plain formatting flags on the text nodes and need no registration, but the
    // set from TEXT_FORMAT_TRANSFORMERS is not used because it also covers inline code ("`"),
    // strikethrough ("~~") and highlight ("=="), which the editor has no styling for. Their order
    // matters: longer tags must match first, and the underline transformer must come last so that
    // "__" maps to underline instead of bold.
    result.push(
      BOLD_ITALIC_STAR,
      BOLD_ITALIC_UNDERSCORE,
      BOLD_STAR,
      BOLD_UNDERSCORE,
      ITALIC_STAR,
      ITALIC_UNDERSCORE,
      UNDERLINE_TRANSFORMER
    );
    if (allowLinks) {
      result.push(LINK);
    }
    return result;
  }, [allowImages, allowLinks, allowLists]);

  useEffect(() => {
    const markdown = value && normalizeLineEndings(value);
    if (markdown && markdown !== markdownRef.current) {
      editor.update(() => {
        $convertFromMarkdownString(markdown, transformers);
      });
    }
  }, [editor, transformers, value]);

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const raw = $convertToMarkdownString(transformers);
        const markdown = fixLineBreaks(raw);
        if (markdownRef.current !== markdown) {
          markdownRef.current = markdown;
          onChange?.(markdown);
        }
      });
    },
    [onChange, transformers]
  );

  return <OnChangePlugin onChange={handleChange} />;
};
